import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Heart, 
  Video, 
  BookOpen,
  Search,
  Filter,
  Calendar,
  User,
  Clock,
  Tag,
  Image,
  X,
  ExternalLink
} from 'lucide-react';
import { API_URLS } from '../../../config/api';

const AdminEducations = () => {
  const [educations, setEducations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    author: '',
    author_role: '',
    author_bio: '',
    read_time: '',
    level: '',
    image: '',
    video_url: '',
    tags: [],
    attachments: [],
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_image: '',
    is_published: true
  });

  const [newTag, setNewTag] = useState('');
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const [mediaPickerMode, setMediaPickerMode] = useState('video'); // 'video' | 'image' | 'og_image'
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);

  useEffect(() => {
    fetchEducations();
    fetchCategories();
  }, []);

  const fetchEducations = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URLS.ADMIN.EDUCATIONS);
      const data = await response.json();
      if (data.status === 'success') {
        setEducations(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch educations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(API_URLS.ADMIN.EDUCATION_CATEGORIES);
      const data = await response.json();
      if (data.status === 'success') {
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchMediaFiles = async () => {
    try {
      setMediaLoading(true);
      const response = await fetch(API_URLS.ADMIN.MEDIA);
      const data = await response.json();
      if (data.status === 'success') {
        // Filter only video files
        const videoFiles = data.data.filter(file => file.type.startsWith('video/'));
        setMediaFiles(videoFiles);
      }
    } catch (error) {
      console.error('Failed to fetch media files:', error);
    } finally {
      setMediaLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.title.trim()) {
      alert('Başlık alanı zorunludur!');
      return;
    }
    if (!formData.slug.trim()) {
      alert('Slug alanı zorunludur!');
      return;
    }
    if (!formData.content.trim()) {
      alert('İçerik alanı zorunludur!');
      return;
    }
    if (!formData.category) {
      alert('Kategori seçimi zorunludur!');
      return;
    }
    
    setSubmitting(true);
    try {
      const url = editingEducation 
        ? `${API_URLS.ADMIN.UPDATE_EDUCATION(editingEducation.id)}`
        : API_URLS.ADMIN.CREATE_EDUCATION;
      
      const method = editingEducation ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        fetchEducations();
        setIsModalOpen(false);
        setEditingEducation(null);
        resetForm();
        alert('Eğitim başarıyla güncellendi!');
      } else {
        console.error('API Error:', data.message);
        alert('Hata: ' + (data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Failed to save education:', error);
      alert('Hata: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (education) => {
    setEditingEducation(education);
    setFormData({
      title: education.title || '',
      slug: education.slug || '',
      excerpt: education.excerpt || '',
      content: education.content || '',
      category: education.category || '',
      author: education.author || '',
      author_role: education.author_role || '',
      author_bio: education.author_bio || '',
      read_time: education.read_time || '',
      level: education.level || '',
      image: education.image || '',
      video_url: education.video_url || '',
      tags: education.tags || [],
      attachments: education.attachments || [],
      meta_title: education.meta_title || '',
      meta_description: education.meta_description || '',
      meta_keywords: education.meta_keywords || '',
      og_image: education.og_image || '',
      is_published: !!education.is_published
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu eğitimi silmek istediğinizden emin misiniz?')) {
      try {
        const response = await fetch(API_URLS.ADMIN.DELETE_EDUCATION(id), {
          method: 'DELETE',
        });
        const data = await response.json();
        if (data.status === 'success') {
          fetchEducations();
        }
      } catch (error) {
        console.error('Failed to delete education:', error);
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await fetch(API_URLS.ADMIN.TOGGLE_EDUCATION_STATUS(id), {
        method: 'PATCH',
      });
      const data = await response.json();
      if (data.status === 'success') {
        fetchEducations();
      }
    } catch (error) {
      console.error('Failed to toggle education status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: '',
      author: '',
      author_role: '',
      author_bio: '',
      read_time: '',
      level: '',
      image: '',
      video_url: '',
      tags: [],
      attachments: [],
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      og_image: '',
      is_published: true
    });
    setNewTag('');
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleMediaSelect = (mediaFile) => {
    if (mediaPickerMode === 'video') {
      setFormData(prev => ({ ...prev, video_url: mediaFile.url }));
    } else if (mediaPickerMode === 'image') {
      setFormData(prev => ({ ...prev, image: mediaFile.url }));
    } else if (mediaPickerMode === 'og_image') {
      setFormData(prev => ({ ...prev, og_image: mediaFile.url }));
    }
    setIsMediaPickerOpen(false);
  };

  const openMediaPicker = (mode) => {
    setMediaPickerMode(mode);
    setIsMediaPickerOpen(true);
    fetchMediaFiles(mode === 'video' ? 'video' : 'image');
  };

  const filteredEducations = educations.filter(education => {
    const matchesSearch = education.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        education.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || education.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Eğitim Yönetimi</h1>
          <p className="text-gray-600">Eğitimleri ekleyin, düzenleyin ve yönetin</p>
        </div>
        <Button 
          onClick={() => {
            setEditingEducation(null);
            resetForm();
            setIsModalOpen(true);
          }}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Eğitim
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Eğitim ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">Tüm Kategoriler</option>
                {categories.map(category => (
                  <option key={category.id} value={category.slug}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <Filter className="w-4 h-4 mr-2" />
              {filteredEducations.length} eğitim bulundu
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Educations List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredEducations.map((education) => (
          <Card key={education.id} className="group hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-gray-900 mb-2 line-clamp-2">
                    {education.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-3 mb-3">
                    {education.excerpt}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {education.video_url && (
                    <Badge variant="outline" className="text-red-600 border-red-600">
                      <Video className="w-3 h-3 mr-1" />
                      Video
                    </Badge>
                  )}
                  <Badge variant={education.is_published ? "default" : "secondary"}>
                    {education.is_published ? 'Yayında' : 'Taslak'}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  <span>{education.author}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{education.read_time}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Heart className="w-4 h-4" />
                  <span>{education.likes}</span>
                </div>
              </div>

              {education.tags && education.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {education.tags.slice(0, 3).map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                  {education.tags.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{education.tags.length - 3}
                    </Badge>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  {formatDate(education.created_at)}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(education)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleStatus(education.id)}
                  >
                    {education.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(education.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto relative">
            <div className="flex justify-between items-start gap-3 mb-4">
              <h2 className="text-xl font-bold break-words flex-1">
                {editingEducation ? 'Eğitimi Düzenle' : 'Yeni Eğitim Ekle'}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingEducation(null);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700 flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
                aria-label="Kapat"
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Başlık *
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug *
                  </label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Özet
                </label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  İçerik *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows="6"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kategori *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="">Kategori Seçin</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.slug}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Yazar
                  </label>
                  <Input
                    value={formData.author}
                    onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Yazar Rolü
                  </label>
                  <Input
                    value={formData.author_role}
                    onChange={(e) => setFormData(prev => ({ ...prev, author_role: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Okuma Süresi
                  </label>
                  <Input
                    value={formData.read_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, read_time: e.target.value }))}
                    placeholder="5 dakika"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Seviye
                  </label>
                  <select
                    value={formData.level}
                    onChange={(e) => setFormData(prev => ({ ...prev, level: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Seviye Seçin</option>
                    <option value="Başlangıç">Başlangıç</option>
                    <option value="Orta">Orta</option>
                    <option value="İleri">İleri</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Görsel
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.image}
                      onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                      placeholder="URL veya medya kütüphanesinden seçin"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => openMediaPicker('image')}
                      className="flex items-center gap-2"
                    >
                      <Image className="w-4 h-4" />
                      Medya Kütüphanesi
                    </Button>
                  </div>
                  {formData.image && (
                    <div className="mt-2 p-2 bg-gray-50 rounded-md flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <img src={formData.image} alt="" className="w-10 h-10 object-cover rounded" />
                        <span className="text-sm text-gray-600 truncate">{formData.image.split('/').pop()}</span>
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setFormData(prev => ({ ...prev, image: '' }))}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Video URL
                </label>
                <div className="flex gap-2">
                  <Input
                    value={formData.video_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                    placeholder="https://www.youtube.com/embed/VIDEO_ID"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => openMediaPicker('video')}
                    className="flex items-center gap-2"
                  >
                    <Video className="w-4 h-4" />
                    Medya Kütüphanesi
                  </Button>
                </div>
                {formData.video_url && (
                  <div className="mt-2 p-2 bg-gray-50 rounded-md flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Video className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600 truncate">
                        {formData.video_url.split('/').pop()}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, video_url: '' }))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dokümanlar (PDF, Word, Excel, TXT)
                </label>
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setAttachmentUploading(true);
                      try {
                        const form = new FormData();
                        form.append('file', file);
                        const res = await fetch(API_URLS.ADMIN.UPLOAD_MEDIA, {
                          method: 'POST',
                          body: form
                        });
                        const data = await res.json();
                        if (data.status === 'success' && data.data) {
                          setFormData(prev => ({
                            ...prev,
                            attachments: [
                              ...(prev.attachments || []),
                              {
                                name: data.data.original_name || file.name,
                                url: data.data.url,
                                type: data.data.type
                              }
                            ]
                          }));
                        } else {
                          alert(data.message || 'Dosya yüklenemedi');
                        }
                      } catch (err) {
                        console.error('Attachment upload failed:', err);
                        alert('Dosya yüklenirken bir hata oluştu');
                      } finally {
                        setAttachmentUploading(false);
                        e.target.value = '';
                      }
                    }}
                    className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                    disabled={attachmentUploading}
                  />
                  {attachmentUploading && (
                    <p className="text-xs text-gray-500">Dosya yükleniyor...</p>
                  )}
                </div>
                {Array.isArray(formData.attachments) && formData.attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {formData.attachments.map((att, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <ExternalLink className="w-4 h-4 text-gray-500" />
                          <a
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-700 hover:underline truncate max-w-xs"
                          >
                            {att.name || att.url}
                          </a>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setFormData(prev => ({
                              ...prev,
                              attachments: prev.attachments.filter((_, i) => i !== idx)
                            }))
                          }
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Etiketler
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Etiket ekle"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" onClick={addTag} variant="outline">
                    Ekle
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <Badge key={index} variant="outline" className="flex items-center gap-1">
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-red-600 hover:text-red-700"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              {/* SEO Ayarları */}
              <div className="border-t pt-4 mt-4 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800">SEO Ayarları</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meta Başlık</label>
                    <Input
                      value={formData.meta_title}
                      onChange={(e) => setFormData(prev => ({ ...prev, meta_title: e.target.value }))}
                      placeholder="Sayfa başlığı (arama sonuçlarında görünür)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meta Açıklama</label>
                    <textarea
                      value={formData.meta_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                      placeholder="Kısa açıklama (arama sonuçlarında görünür)"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meta Anahtar Kelimeler</label>
                    <Input
                      value={formData.meta_keywords}
                      onChange={(e) => setFormData(prev => ({ ...prev, meta_keywords: e.target.value }))}
                      placeholder="kelime1, kelime2, kelime3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">OG Görsel (sosyal paylaşım)</label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.og_image}
                        onChange={(e) => setFormData(prev => ({ ...prev, og_image: e.target.value }))}
                        placeholder="URL veya medya kütüphanesinden seçin"
                        className="flex-1"
                      />
                      <Button type="button" variant="outline" onClick={() => openMediaPicker('og_image')} className="flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Medya Kütüphanesi
                      </Button>
                    </div>
                    {formData.og_image && (
                      <div className="mt-2 p-2 bg-gray-50 rounded-md flex items-center gap-2">
                        <img src={formData.og_image} alt="" className="w-12 h-12 object-cover rounded" />
                        <span className="text-sm text-gray-600 truncate flex-1">{formData.og_image.split('/').pop()}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setFormData(prev => ({ ...prev, og_image: '' }))}><X className="w-4 h-4" /></Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_published}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                    className="mr-2"
                  />
                  Yayınla
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingEducation(null);
                    resetForm();
                  }}
                >
                  İptal
                </Button>
                <Button 
                  type="submit" 
                  className="bg-purple-600 hover:bg-purple-700"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {editingEducation ? 'Güncelleniyor...' : 'Oluşturuluyor...'}
                    </>
                  ) : (
                    editingEducation ? 'Güncelle' : 'Oluştur'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Media Picker Modal */}
      {isMediaPickerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">
                Medya Kütüphanesi – {mediaPickerMode === 'video' ? 'Video Seç' : mediaPickerMode === 'og_image' ? 'OG Görsel Seç' : 'Resim Seç'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMediaPickerOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {mediaLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : mediaFiles.length === 0 ? (
                <div className="text-center py-12">
                  {mediaPickerMode === 'video' ? <Video className="h-12 w-12 mx-auto text-gray-400 mb-4" /> : <Image className="h-12 w-12 mx-auto text-gray-400 mb-4" />}
                  <p className="text-lg font-medium text-gray-500">
                    {mediaPickerMode === 'video' ? 'Video bulunamadı' : 'Resim bulunamadı'}
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    {mediaPickerMode === 'video' ? 'Medya kütüphanesinde video dosyası bulunmuyor' : 'Medya kütüphanesinde resim dosyası bulunmuyor'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {mediaFiles.map((file) => (
                    <div
                      key={file.name}
                      className="border rounded-lg p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => handleMediaSelect(file)}
                    >
                      <div className="aspect-video bg-gray-100 rounded-md mb-2 flex items-center justify-center overflow-hidden">
                        {mediaPickerMode === 'video' ? (
                          <video src={file.url} className="w-full h-full object-cover rounded-md" muted />
                        ) : (
                          <img src={file.url} alt="" className="w-full h-full object-cover rounded-md" />
                        )}
                      </div>
                      <div className="text-sm">
                        <p className="font-medium truncate" title={file.name}>{file.name}</p>
                        <p className="text-gray-500 text-xs">{(file.size / 1024).toFixed(0)} KB</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setIsMediaPickerOpen(false)}
              >
                İptal
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEducations;
